/**
 * The type-meta line: the type pill, followed by the facts that only that type
 * has.
 *
 * ```
 * [Company Map]  Criteria: Medallia · Run by: joseliendro@nusearch.com
 * [AI Review]    Reviewed by lucas@nusearch.com
 * [Raw Import]   Uploaded by patrick@nusearch.com · 8/25/2026
 * ```
 *
 * The pill moved down here from beside the title on 2026-08-26, to give the
 * title row back to the Search block. The reason it works — and the reason the
 * first sketch of it did NOT — is the raw case: with only Company Map and AI
 * Review carrying a tail, a raw list would render a pill alone on a line, and
 * the badge would appear to change position depending on the type. A badge
 * whose whole job is to say "what kind of list is this" must not move by kind.
 * So raw gets a real tail (who uploaded it, and when), and all three variants
 * take the same shape.
 *
 * The left accent stripe stays on the title. Stripe and pill still read from
 * the ONE derived type tag, so they cannot disagree; the stripe carries the
 * identity visually up top, this names it down here.
 */
import type { TagDef } from "../imports/tagDefs";

export function TypeMetaLine({
  typeDef,
  label,
  children,
}: {
  /** The derived type's def — `undefined` while the detail is still loading. */
  typeDef?: TagDef;
  /** The pill's text when it is not the def's bare label — `typeLabel`'s
   *  "Raw Import · Thrive" for a list pulled from a Thrive search. */
  label?: string;
  /** The per-type tail. */
  children?: React.ReactNode;
}) {
  if (!typeDef && !children) return null;

  return (
    <div
      data-testid="type-meta-line"
      // NO bottom margin. This used to be a standalone row under the header
      // and carried `mb-4`; it now lives INSIDE the header's flex column, which
      // supplies its own `gap-y`. The leftover margin stacked with the header's
      // own `mb-4` and showed up as a hole above the next section — visible on
      // AI Review and Raw Import, and hidden on Company Map only because
      // `CriteriaBox` happened to fill it.
      className="flex flex-wrap items-baseline gap-x-3 gap-y-1"
    >
      {typeDef && (
        <span
          data-testid="import-type-badge"
          className={`inline-flex items-center rounded-pill px-3 py-1 text-caption ${typeDef.pillClass}`}
        >
          {label ?? typeDef.label}
        </span>
      )}
      {children}
    </div>
  );
}

/** The raw-list tail: who put the file here, and when. */
export function RawImportMeta({
  uploader,
  createdAt,
}: {
  uploader: string | null | undefined;
  createdAt: string;
}) {
  return (
    <span
      data-testid="raw-import-meta"
      // `text-caption` (11px). This slot used `text-body-sm`, which is NOT a
      // defined fontSize key in either tailwind config (the scale is
      // display/h1/h2/h3/body/label/caption/micro/nano) and generated no CSS —
      // and since no root `font-size` is declared anywhere, it inherited the
      // BROWSER DEFAULT of 16px rather than the 13px body it reads as.
      //
      // Changed here because cl.5 shrank the AI Review tail and would otherwise
      // have left three tails in one row at two different sizes — 11px and
      // 16px — in the component whose docstring exists to say a badge "must not
      // move by kind" (round 9, Agent A §2.3). Seven OTHER call sites in
      // `packages/web` carry the same dead class and are deliberately untouched:
      // they are not in this row and not this card's business.
      className="flex flex-wrap items-baseline gap-x-2 text-caption text-text"
    >
      <span className="text-text-secondary">Uploaded by</span>
      <span>{uploader ?? "—"}</span>
      <span aria-hidden="true" className="text-text-muted">
        ·
      </span>
      <span>{new Date(createdAt).toLocaleDateString()}</span>
    </span>
  );
}

/** The AI-Review tail: who ran it.
 *
 * The annexed column names are NOT here (2026-08-26). They were, and they blew
 * the line open — five criterion names is a sentence, not a label, and it
 * pushed `Reviewed by` off the readable part of the row. They already have a
 * better home a few pixels below: `CriteriaBox` renders one chip per column,
 * which is shorter to scan. (This used to name `AnnexBar` and call the chips
 * clickable; that component was deleted in this same PR and its chips were
 * never clickable — /pr-review iter 2 §8.2.)
 */
export function AiReviewMeta({ reviewedBy }: { reviewedBy: string | null | undefined }) {
  return (
    <span
      data-testid="ai-review-meta"
      // `text-caption` (11px), not `text-body-sm` — that class is not a
      // defined fontSize token in either tailwind config (the scale is
      // display/h1/h2/h3/body/label/caption/micro/nano), so it generated
      // nothing and this line simply inherited. Ten other call sites still use
      // it; they are not this card's business.
      className="flex flex-wrap items-baseline gap-x-2 text-caption text-text"
    >
      <span className="text-text-secondary">Reviewed by</span>
      <span>{reviewedBy ?? "—"}</span>
    </span>
  );
}

/** The tail of a list pulled from a Thrive search (04): the SOURCE search it
 *  was pulled from, who pulled it and when. The search card beside it names
 *  the search the list is linked to, which may be another one. */
export function ThriveImportMeta({
  originSearchId,
  originSearchName,
  pulledBy,
  createdAt,
}: {
  originSearchId: string | null | undefined;
  originSearchName: string | null | undefined;
  pulledBy: string | null | undefined;
  createdAt: string;
}) {
  const name = originSearchName?.trim();
  return (
    <span
      data-testid="thrive-import-meta"
      className="flex flex-wrap items-baseline gap-x-2 text-caption text-text"
    >
      <span className="text-text-secondary">Pulled from</span>
      <span data-testid="thrive-import-origin">
        Thrive search {originSearchId ?? "—"}
        {name ? ` · ${name}` : ""}
      </span>
      <span className="text-text-secondary">by</span>
      <span>{pulledBy ?? "—"}</span>
      <span aria-hidden="true" className="text-text-muted">
        ·
      </span>
      <span>{new Date(createdAt).toLocaleDateString()}</span>
    </span>
  );
}
